import { useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';

/**
 * Signs an idle user out. Per Phase 2 §6.2: 30-minute idle timeout with a
 * "you'll be signed out in 1 minute" warning toast. Only armed while a user is
 * authenticated. Any real interaction (pointer, key, scroll, tab focus) resets
 * the clock and dismisses the warning.
 *
 * Uses a low-frequency interval that compares against a last-activity
 * timestamp rather than a chain of setTimeouts — cheap per event, and robust
 * to a tab being backgrounded/throttled.
 */
const IDLE_LIMIT_MS = 30 * 60 * 1000;
const WARNING_LEAD_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
const WARNING_TOAST_ID = 'idle-timeout-warning';
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'visibilitychange',
] as const;

export function useIdleLogout(): void {
  const { isAuthenticated, signOut } = useAuth();
  const { t } = useTranslation();
  const lastActivityRef = useRef<number>(Date.now());
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    lastActivityRef.current = Date.now();
    warnedRef.current = false;

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      if (warnedRef.current) {
        warnedRef.current = false;
        notifications.hide(WARNING_TOAST_ID);
      }
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    const interval = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= IDLE_LIMIT_MS) {
        notifications.hide(WARNING_TOAST_ID);
        notifications.show({ message: t('auth.idle.loggedOut'), color: 'orange' });
        signOut();
      } else if (idleMs >= IDLE_LIMIT_MS - WARNING_LEAD_MS && !warnedRef.current) {
        warnedRef.current = true;
        notifications.show({
          id: WARNING_TOAST_ID,
          title: t('auth.idle.warningTitle'),
          message: t('auth.idle.warning'),
          color: 'yellow',
          autoClose: false,
        });
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      window.clearInterval(interval);
      notifications.hide(WARNING_TOAST_ID);
    };
  }, [isAuthenticated, signOut, t]);
}
